import { useEffect, useState } from "react";
import { api, dateTime } from "../../api.js";
import { useResource } from "../useResource.js";
import { shortName } from "../common.jsx";
import { loadKey, signHash } from "../../kep.js";
import "./pn.css";
import "../requests/requests.css";
import CertField from "../../CertField.jsx";

// «Отримані РК» — як у кабінеті ДПС (Реєстрація ПН/РК → Отримані РК): розрахунки коригування, які продавець
// надіслав вам як покупцю. РК, що зменшує податкове зобов'язання продавця, реєструє покупець: підписує своїм КЕП
// і надсилає на реєстрацію. Ключ і пароль залишаються в браузері — на сервер іде лише підпис хешу документа
const iso = (d) => d.toLocaleDateString("sv-SE", { timeZone: "Europe/Kyiv" });

export default function PnReceivedRk({ companyId, company }) {
  const now = new Date();
  const [from, setFrom] = useState(iso(new Date(now.getFullYear(), now.getMonth() - 2, 1)));
  const [to, setTo] = useState(iso(now));
  const [unread, setUnread] = useState(false);
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState(null);
  const [reload, setReload] = useState(0);
  const r = useResource(`/api/companies/${companyId}/pn/received-rk?from=${from}&to=${to}&notDeliver=${unread}&page=${page}`, reload);
  const pages = r.data ? Math.ceil(r.data.total / (r.data.pageSize || 10)) : 0;
  if (open) return <RkDetail companyId={companyId} company={company} id={open} onBack={() => { setOpen(null); setReload((x) => x + 1); }} />;
  return (
    <div className="pn-reg">
      <p className="muted small">Розрахунки коригування, які надіслали вам продавці. РК, що зменшує податкове зобов'язання продавця, реєструє покупець: відкрийте РК, перегляньте й підпишіть ключем компанії — eCabinet надішле його на реєстрацію.</p>
      <div className="row pn-filters">
        <label className="field field-inline">З<input type="date" value={from} max={to} onChange={(e) => { setFrom(e.target.value); setPage(0); }} /></label>
        <label className="field field-inline">По<input type="date" value={to} min={from} onChange={(e) => { setTo(e.target.value); setPage(0); }} /></label>
        <label className="check"><input type="checkbox" checked={unread} onChange={(e) => { setUnread(e.target.checked); setPage(0); }} />Лише непрочитані</label>
      </div>
      {r.error ? <p className="error" role="alert">{r.error.message}</p> : r.pending ? <p className="muted" role="status">Читаємо кабінет…</p> : !r.data.items.length ? (
        <p className="muted">Отриманих РК за цей період немає.</p>
      ) : (
        <>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Надійшов</th><th>Код продавця</th><th>Продавець</th><th>Документ</th><th>Прочитано</th><th><span className="sr-only">Дія</span></th></tr></thead>
              <tbody>
                {r.data.items.map((x, i) => (
                  <tr key={x.id ?? i} className={x.id ? "clickable" : undefined} onClick={() => x.id && setOpen(String(x.id))}>
                    <td>{dateTime(x.incomeDt)}</td><td>{x.sellerTin}</td><td title={x.sellerName}>{shortName(x.sellerName)}</td>
                    <td className="mono-sm">{x.docId}</td><td>{x.deliverDt ? dateTime(x.deliverDt) : <span className="badge badge-warn">ні</span>}</td>
                    <td>{x.id && <button type="button" className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); setOpen(String(x.id)); }}>Відкрити</button>}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div className="pager">
              <button type="button" className="btn btn-secondary btn-sm" aria-label="Попередня сторінка" disabled={page === 0} onClick={() => setPage(page - 1)}>‹</button>
              <span className="muted">{page + 1} з {pages} · {r.data.total}</span>
              <button type="button" className="btn btn-secondary btn-sm" aria-label="Наступна сторінка" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>›</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RkDetail({ companyId, company, id, onBack }) {
  const base = `/api/companies/${companyId}/pn/received-rk/${id}`;
  const [d, setD] = useState(null), [error, setError] = useState(""), [busy, setBusy] = useState("");
  const [part, setPart] = useState(null);
  const [keyFile, setKeyFile] = useState(null), [certFiles, setCertFiles] = useState([]), [password, setPassword] = useState("");
  const allowed = ["submitter", "owner"].includes(company?.role);
  useEffect(() => {
    let active = true;
    api(base).then((x) => { if (active) { setD(x); setPart(x.parts[0]?.cd || null); } }, (e) => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [base]);
  const run = async (label, fn) => {
    if (busy) return;
    setBusy(label); setError("");
    try { const x = await fn(); if (x) setD(x); } catch (e) { setError(e.message); } finally { setBusy(""); setPassword(""); }
  };
  const sign = (e) => {
    e.preventDefault();
    run("Відкриваємо ключ…", async () => {
      const { box, edrpou } = await loadKey({ keyFile, certFiles, password, log: setBusy });
      if (company?.edrpou && edrpou !== company.edrpou) throw new Error("Ключ належить іншій компанії");
      setBusy("Отримуємо документи для підпису…");
      const { items } = await api(`${base}/hashes`);
      setBusy("Підписуємо…");
      const signed = [];
      for (const x of items) signed.push({ ...x, signature: await signHash(box, x.hash) });
      setBusy("Надсилаємо на реєстрацію…");
      return api(`${base}/sign`, { method: "POST", body: { items: signed, send: true } });
    });
  };
  const sent = d && d.sent !== null;
  return (
    <section className="erpn-panel request-sign" aria-label="Отриманий РК">
      <div className="row">
        <div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onBack}>← До списку</button>
          <h3>Отриманий РК{d?.sellerName ? ` від ${shortName(d.sellerName)}` : ""}</h3>
          {d && <p className="muted small">Продавець {d.sellerTin} · надійшов {dateTime(d.incomeDt)}{d.deliverDt ? ` · прочитано ${dateTime(d.deliverDt)}` : ""} · підписів: {d.signCnt}</p>}
        </div>
        {d && <div className="erpn-files">
          {part && <a className="btn btn-secondary btn-sm" href={`${base}/parts/${part}/pdf?inline=1`} target="_blank" rel="noreferrer">Переглянути PDF</a>}
          {part && <a className="btn btn-ghost btn-sm" href={`${base}/parts/${part}/pdf`}>⬇ PDF</a>}
          {d.kvtId && <a className="btn btn-ghost btn-sm" href={`${base}/kvt`}>⬇ Квитанція</a>}
        </div>}
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      {!d && !error && <p className="muted" role="status">Читаємо кабінет…</p>}
      {d && sent && <p className="notice">РК надіслано на реєстрацію ({dateTime(d.sent)}). Стан реєстрації — у квитанції{d.kvtId ? "" : ", вона з'явиться згодом"}.</p>}
      {d && d.parts.length > 1 && (
        <div className="tabs" role="group" aria-label="Документ пакета">
          {d.parts.map((x) => <button key={x.cd} type="button" className={`tab${part === x.cd ? " active" : ""}`} aria-pressed={part === x.cd} onClick={() => setPart(x.cd)}>{x.cdoc || x.cd}</button>)}
        </div>
      )}
      {part && <div className="request-pdf"><iframe key={part} src={`${base}/parts/${part}/pdf?inline=1`} title="Розрахунок коригування — PDF ДПС" /></div>}
      {d && !sent && d.signCnt >= 1 && (
        <p className="muted small">Уже підписали РК (наприклад, у кабінеті ДПС), але не надіслали?{" "}
          <button type="button" className="btn btn-secondary btn-sm" disabled={!allowed || !!busy} onClick={() => run("Надсилаємо на реєстрацію…", () => api(`${base}/send`, { method: "POST" }))}>Надіслати без нового підпису</button></p>
      )}
      {d && !sent && (
        <form onSubmit={sign} className="card">
          <h3>Підписати й надіслати на реєстрацію</h3>
          <p className="muted small">Перегляньте РК вище. Підписуєте ключем компанії-покупця; ключ і пароль залишаються у браузері. Після підпису РК одразу надсилається на реєстрацію в ЄРПН.</p>
          <div className="request-fields">
            <label className="field">Файл ключа<input type="file" accept=".dat,.jks,.pfx,.zs2,.sk" required disabled={!!busy} onChange={(e) => setKeyFile(e.target.files[0])} /></label>
            <label className="field">Пароль ключа<input type="password" autoComplete="off" value={password} required disabled={!!busy} onChange={(e) => setPassword(e.target.value)} /></label>
            <CertField onChange={setCertFiles} disabled={!!busy} />
          </div>
          <button type="submit" className="btn" disabled={!allowed || !keyFile || !password || !!busy}>Підписати й надіслати</button>
        </form>
      )}
      {busy && <p role="status">{busy}</p>}
      {d && !sent && !allowed && <p className="muted">Підписувати й надсилати може власник або користувач із роллю «подання».</p>}
    </section>
  );
}
